/**
 * Shared helpers for turning persisted conversation rows into chat Messages.
 *
 * Used by BOTH the WebSocket first-page restore (HISTORY_RESTORED) and the REST
 * scroll-up pagination (older pages), so the two paths produce identical Messages
 * and merge without drift.
 */

import {
  ComponentType,
  MessageRole,
  MessageType,
  type AgentAttachment,
  type ContactSummary,
  type Message,
  type ToolTimeoutOfferState,
} from '@/types/chat';
import type { ConversationMessage } from '@/types/conversation';
import { mapPersistedResultToMessage } from '@/lib/tool-result-mapper';
import { searchedLocationFromArgs } from '@/lib/course-grouping';
import { SupportRequestType } from '@/lib/api/support-requests';

// Only `id`/`content` are derived from the generated ConversationMessage —
// every other field is deliberately loosened relative to the wire type, so
// re-declaring it here (rather than inheriting it) is the honest shape:
//   - role/message_type: narrowed to `string` (not the MessageRole/MessageType
//     enums) for defensive parsing — see the field comments below.
//   - metadata: a bespoke restore-specific shape, not the generic
//     `Record<string, unknown> | null` the wire type carries.
//   - seq/created_at: ConversationMessage marks both REQUIRED (the backend
//     has always sent them), but this row arrives via two different paths
//     (WS restore AND REST pagination) and is read defensively rather than
//     validated — see created_at's comment below. Extending straight from
//     ConversationMessage with these still required, then trying to
//     re-declare them optional here, is a TS2430 interface-extension error
//     (a subtype can't loosen an inherited required property) — confirmed by
//     trying it. Excluding them from the derived base and keeping them
//     optional here is the correct fix, not a cast/workaround.
type RestoredRowBase = Omit<
  ConversationMessage,
  'role' | 'message_type' | 'metadata' | 'seq' | 'created_at'
>;

/** One persisted message row as it arrives from the backend (WS or REST). */
export interface RestoredRow extends RestoredRowBase {
  role: string;
  message_type: string;
  seq?: number;
  /** ISO timestamp of the persisted row. The backend has always sent it on
   *  both restore paths (repository `_decrypt_row`); optional here because the
   *  wire type is unvalidated. Read defensively → `Message.createdAt`. */
  created_at?: string;
  // Tool rows carry { kind, payload }; text rows have null/absent metadata.
  // NEW backend (tool-call-lifecycle persistence): every tool invocation is
  // persisted as a TOOL_CALL row (message_type 'tool_call') carrying
  // metadata.call = { tool, call_id, status, started_at, completed_at?,
  // arguments? }. When status === 'completed' AND metadata.kind is present,
  // metadata ALSO carries { kind, payload } as siblings of `call` — same shape
  // as an OLD-backend TOOL_RESULT row, so it renders the identical card (see
  // the mapping in restoredMessagesToMessages below). OLD backend rows never
  // set `call`; both shapes are handled so this file works against either
  // backend during the migration window.
  // HUMAN_AGENT rows carry agent_display_name (Decision #8/#9,
  // 2026-07-hubspot-native-handoff.md — ConversationMessage.metadata is a
  // generic Optional[dict], no schema change needed). rich_text/attachments
  // are read defensively (optional) — the plan documents live delivery of
  // both but is silent on whether the persistence layer stores them for
  // restore; if the backend never populates them, this degrades gracefully
  // to plain-text restore instead of failing.
  // `internal` (2026-07-06 field-testing fix): the backend now persists
  // SYSTEM rows that are Sara-facing projection markers, not user-facing
  // notices — e.g. "[Handoff record] ... recall_handoff_conversation ...",
  // written so a later agent turn can recall handoff context. These must
  // NEVER render; see the filter below.
  // Decision #18 (2026-07-hubspot-native-handoff.md): the post-submit
  // "Details shared" summary card is persisted as a SYSTEM row whose
  // metadata carries `component: "contact_summary"` plus the submitted
  // fields (see backend's `persist_contact_summary`, agent/manager.py).
  // `component` is the discriminant this mapper switches on — it can't
  // reuse `kind` above because that's TOOL_RESULT-row-specific and this is a
  // plain TEXT row.
  metadata?: {
    kind?: string;
    payload?: unknown;
    agent_display_name?: string;
    rich_text?: string | null;
    attachments?: AgentAttachment[];
    internal?: boolean;
    component?: string;
    name?: string;
    email?: string;
    phone?: string | null;
    /** `handoff_timeout_offer` rows: the handoff this offer belongs to.
     *  `support_request_submitted` rows: the handoff this receipt fulfilled
     *  (null/absent on a marker persisted before the backend started
     *  including it, or on a request submitted with no handoff on hand —
     *  see handoff-timeout-restore.ts for how that's treated). */
    handoff_id?: string | null;
    /** `support_request_submitted` rows only — the persisted request's id.
     *  Not currently consumed by this mapper (no UI needs it yet), but kept
     *  on the type so a malformed-metadata test can assert it round-trips
     *  like every other field on this marker. */
    support_request_id?: string;
    /** `support_request_submitted` rows only — the wire value of
     *  SupportRequestType ('message' | 'callback'). Validated against the
     *  actual enum (not just "is a string") before it drives rendering. */
    type?: string;
    /** `tool_timeout_offer` rows only (session-recovery) — mirrors
     *  ToolTimeoutOfferEvent's own fields exactly (see
     *  RealtimeWebSocketManager._persist_tool_timeout_offer). `call_id` was
     *  removed from the wire entirely (see ToolTimeoutOfferState's own doc
     *  comment in types/chat.ts) — `retry_id` is what now does the
     *  correlation job it was reserved for, and IS read here (a restored
     *  offer with no retry_id is unparseable — see the guard below).
     *  `tool_label` was ALSO removed (copy review — see ToolTimeoutOfferState's
     *  own doc comment for the full "why"; the backend drops it from this
     *  persisted metadata in parallel with the wire event). */
    tool_name?: string;
    attempt?: number;
    retry_available?: boolean;
    retry_id?: string;
    /** NEW backend only — the tool-call lifecycle descriptor for a TOOL_CALL
     *  row (see the block comment above). `status` mirrors the backend's
     *  persisted lifecycle states; `completed_empty` is a completed call with
     *  nothing renderable (matches today's empty-payload skip). `arguments`
     *  are already decrypted server-side (a plain object) — find_courses'
     *  zip_code/city/state feed `searchedLocationFromArgs` the same way the
     *  live tool_end path does. */
    call?: {
      tool: string;
      call_id: string;
      status:
        | 'running'
        | 'completed'
        | 'failed'
        | 'completed_empty'
        | 'abandoned';
      started_at: string;
      completed_at?: string;
      arguments?: Record<string, unknown>;
    };
  } | null;
}

/** Defensive ISO → epoch-ms parse; undefined on absent/garbage input.
 *
 *  A timestamp with NO timezone suffix is treated as UTC: the backend's
 *  `created_at` (and pre-fix `call.started_at`) serialize naive-UTC datetimes
 *  whose isoformat carries no suffix, and JS `Date.parse` reads a naive ISO as
 *  LOCAL time — mixing those with the timezone-aware `call.completed_at`
 *  skewed restored "Worked for Xs" spans by the viewer's UTC offset
 *  (caught live in the tool-persistence E2E, 2026-07-13). */
function parseIso(iso?: string | null): number | undefined {
  if (!iso) return undefined;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(ms) ? undefined : ms;
}

function parseCreatedAt(row: RestoredRow): number | undefined {
  return parseIso(row.created_at);
}

/** find_courses' searched-location passthrough (Task brief, "new wire
 *  contract") — restored panels otherwise lose the "near 07029" header
 *  because arguments aren't persisted on OLD-backend TOOL_RESULT rows.
 *  No-op for any other card kind or when the args carry no location. */
function withSearchedLocation(
  card: Message,
  args: Record<string, unknown> | undefined
): Message {
  if (card.result?.kind !== ComponentType.COURSE_BROWSER) return card;
  const location = searchedLocationFromArgs(args) ?? undefined;
  if (!location) return card;
  return { ...card, result: { ...card.result, searchedLocation: location } };
}

/**
 * Map a page of persisted rows (already ordered by seq ascending) to Messages.
 *   - TOOL_RESULT rows → a card via the shared mapper (identical to the live card);
 *     unrenderable ones (empty) are dropped.
 *   - text rows → a bubble; genuinely-empty text rows are skipped.
 * Message.id is the stable DB id so pages merge/dedup by id without collisions.
 */
export function restoredMessagesToMessages(rows: RestoredRow[]): Message[] {
  const parsed: Message[] = [];
  for (const m of rows) {
    // Backend-internal projection marker (Sara-facing only, never user-facing)
    // — drop before any other mapping. Guarded on role, not message_type: these
    // are marker rows and the plan is silent on which message_type they're
    // persisted under, so gating on the metadata flag alone is the robust check.
    if (m.role === MessageRole.SYSTEM && m.metadata?.internal) continue;
    if (m.message_type === MessageType.TOOL_RESULT && m.metadata?.kind) {
      const card = mapPersistedResultToMessage(
        m.metadata.kind,
        m.metadata.payload,
        m.id
      );
      // Mark provenance so live UI (e.g. the responding indicator) can tell a
      // rehydrated message from one produced this session.
      if (card)
        parsed.push({ ...card, restored: true, createdAt: parseCreatedAt(m) });
      continue;
    }
    // NEW backend: every tool invocation is a TOOL_CALL row (see the
    // metadata.call doc comment on RestoredRow above).
    if (m.message_type === MessageType.TOOL_CALL) {
      const call = m.metadata?.call;
      // Malformed/pre-migration row with no `call` descriptor — nothing to
      // derive a card or an outcome chip from. Drop rather than falling
      // through to the generic role/content handling below, which would
      // render a stray bubble for the backend's internal 'tool' role.
      if (!call) continue;

      if (call.status === 'completed' && m.metadata?.kind) {
        const card = mapPersistedResultToMessage(
          m.metadata.kind,
          m.metadata.payload,
          m.id
        );
        if (card) {
          parsed.push({
            ...withSearchedLocation(card, call.arguments),
            restored: true,
            createdAt: parseCreatedAt(m),
            completedAt: parseIso(call.completed_at),
          });
        }
        continue;
      }

      if (
        call.status === 'running' ||
        call.status === 'failed' ||
        call.status === 'abandoned'
      ) {
        parsed.push({
          role: MessageRole.SYSTEM,
          id: m.id,
          restored: true,
          toolOutcome: { tool: call.tool, status: call.status },
          createdAt: parseCreatedAt(m),
        });
      }
      // `completed_empty`, or `completed` with no `kind` (a completed call
      // with nothing renderable) — nothing to show; drop silently, same as
      // the empty-payload skip in the TOOL_RESULT branch above.
      continue;
    }
    if (!m.content) continue;
    // Decision #18.1: a restored "Details shared" summary card. Guarded on
    // name/email actually being non-empty strings (not just the component
    // discriminant) — this is the fallback-safety path: if the metadata is
    // somehow malformed/incomplete, `contactSummary` stays unset and the row
    // falls through to the generic SYSTEM push below, which still renders
    // `m.content` (the backend's own pre-formatted "Details shared — …"
    // string) as a plain notice. Never silently drops the row.
    if (
      m.role === MessageRole.SYSTEM &&
      m.metadata?.component === 'contact_summary' &&
      typeof m.metadata?.name === 'string' &&
      m.metadata.name.length > 0 &&
      typeof m.metadata?.email === 'string' &&
      m.metadata.email.length > 0
    ) {
      const contactSummary: ContactSummary = {
        name: m.metadata.name,
        email: m.metadata.email,
        phone: m.metadata.phone ?? undefined,
      };
      parsed.push({
        role: MessageRole.SYSTEM,
        content: m.content,
        contactSummary,
        id: m.id,
        restored: true,
        createdAt: parseCreatedAt(m),
      });
      continue;
    }
    // Outstanding "no agent available" offer (agent/manager.py's
    // persist_handoff_timeout_offer). Guarded on handoff_id being a
    // non-empty string — same fallback-safety shape as contact_summary
    // above: a malformed row still renders `m.content` as a plain notice, it
    // just won't be findable by outstandingTimeoutOffer
    // (handoff-timeout-restore.ts), so page.tsx can't re-mount the recovery
    // panel for it. Deliberately NOT rendered inline — see
    // handoffTimeoutOffer's doc comment on Message (types/chat.ts).
    //
    // `content` is dropped on the SUCCESSFUL parse (unlike every other
    // branch here, and unlike the malformed fallback below it). Two reasons,
    // both about not saying the same thing three times on reload:
    //   1. The timeout watchdog ALSO persists a personalized apology via
    //      persist_user_notice (relay.py `_deadline_watchdog`), which is the
    //      real user-facing prose for this moment and restores on its own row.
    //   2. This marker's content — "No agent was available — offered to
    //      leave a message or request a call-back." — is written in the third
    //      person ABOUT the system. It's a log line, not copy, and it never
    //      appears on the live path (the marker is persisted, never sent as a
    //      frame). Rendering it only on restore would make a reloaded
    //      transcript read differently from the live one.
    // The panel this row summons carries its own "No guide could join in
    // time" chip, so nothing is lost by staying silent here.
    if (
      m.role === MessageRole.SYSTEM &&
      m.metadata?.component === 'handoff_timeout_offer' &&
      typeof m.metadata?.handoff_id === 'string' &&
      m.metadata.handoff_id.length > 0
    ) {
      parsed.push({
        role: MessageRole.SYSTEM,
        handoffTimeoutOffer: { handoffId: m.metadata.handoff_id },
        id: m.id,
        restored: true,
        createdAt: parseCreatedAt(m),
      });
      continue;
    }
    // Outstanding session-recovery tool-timeout offer
    // (RealtimeWebSocketManager._persist_tool_timeout_offer) — mirrors the
    // handoff_timeout_offer branch immediately above exactly, down to
    // dropping `content` on a successful parse: this marker's copy (a log
    // line about the system) is not user-facing prose. Sara's own
    // in-character apology (a separate, ordinary assistant turn) already
    // covers that on both the live AND restored transcript, so staying
    // silent here doesn't lose anything. Guarded on tool_name being a
    // non-empty string and attempt/retry_available carrying their expected
    // types — same fallback-safety shape as every other branch here: a
    // malformed row still falls through to the generic SYSTEM push below,
    // rendering `m.content` as a plain notice, and just won't be findable
    // by outstandingToolTimeoutOffer (lib/tool-timeout-restore.ts), so
    // page.tsx can't re-mount the card for it. `tool_label` is no longer
    // read here at all (copy review — see ToolTimeoutOfferState's own doc
    // comment).
    if (
      m.role === MessageRole.SYSTEM &&
      m.metadata?.component === 'tool_timeout_offer' &&
      typeof m.metadata?.tool_name === 'string' &&
      m.metadata.tool_name.length > 0 &&
      typeof m.metadata?.attempt === 'number' &&
      typeof m.metadata?.retry_available === 'boolean' &&
      typeof m.metadata?.retry_id === 'string' &&
      m.metadata.retry_id.length > 0
    ) {
      const toolTimeoutOffer: ToolTimeoutOfferState = {
        toolName: m.metadata.tool_name,
        attempt: m.metadata.attempt,
        retryAvailable: m.metadata.retry_available,
        retryId: m.metadata.retry_id,
      };
      parsed.push({
        role: MessageRole.SYSTEM,
        toolTimeoutOffer,
        id: m.id,
        restored: true,
        createdAt: parseCreatedAt(m),
      });
      continue;
    }
    // Support-request submission receipt (api/support.py's
    // create_support_request marker). Guarded on `type` being an actual
    // SupportRequestType value (not just "is a string") — same
    // fallback-safety shape as the two branches above. `handoff_id` is
    // read permissively (may be legitimately absent/null — see the
    // RestoredRow doc comment) since outstandingTimeoutOffer treats a
    // handoff-id-less receipt as fulfilling ANY earlier outstanding offer,
    // not as a malformed row.
    if (
      m.role === MessageRole.SYSTEM &&
      m.metadata?.component === 'support_request_submitted' &&
      Object.values(SupportRequestType).includes(
        m.metadata?.type as SupportRequestType
      )
    ) {
      parsed.push({
        role: MessageRole.SYSTEM,
        content: m.content,
        supportRequestSubmitted: {
          type: m.metadata.type as SupportRequestType,
          handoffId: m.metadata.handoff_id ?? undefined,
        },
        id: m.id,
        restored: true,
        createdAt: parseCreatedAt(m),
      });
      continue;
    }
    if (m.role === MessageRole.HUMAN_AGENT) {
      parsed.push({
        role: MessageRole.HUMAN_AGENT,
        content: m.content,
        richText: m.metadata?.rich_text ?? undefined,
        agentDisplayName: m.metadata?.agent_display_name,
        attachments: m.metadata?.attachments,
        id: m.id,
        restored: true,
        createdAt: parseCreatedAt(m),
      });
      continue;
    }
    parsed.push({
      role: m.role as Message['role'],
      content: m.content,
      id: m.id,
      restored: true,
      createdAt: parseCreatedAt(m),
    });
  }
  // Message-persisted id bridge (see Message.dbId in types/chat.ts): a
  // restored row's client-visible `id` already IS the DB UUID — the backend
  // wrote it as `messages.id` before this restore payload was ever built.
  // Stamping `dbId` here, once, for every branch above (rather than
  // repeating `dbId: m.id` in each of the eight pushes) is the SAME "one
  // rule, no special cases" the field's doc comment asks for, just applied
  // at the function's single exit point instead of at each call site.
  return parsed.map((m) => ({ ...m, dbId: m.id }));
}

/**
 * Prepend an OLDER page to the existing messages, deduping by id at the boundary
 * (a cursor overlap or a re-fetch must never produce a duplicate bubble/card).
 * Order is preserved: [older…, existing…].
 */
export function mergePrepend(older: Message[], existing: Message[]): Message[] {
  const existingIds = new Set(existing.map((m) => m.id));
  const fresh = older.filter((m) => !existingIds.has(m.id));
  if (fresh.length === 0) return existing;
  return [...fresh, ...existing];
}

/**
 * Index of the first restored message that belongs to the CURRENT call —
 * `messages.slice(idx)` is what the transcript rail should show after a
 * mid-call reconnect replaces the whole message list (HISTORY_RESTORED,
 * FB28: the call now survives a socket drop). The rail's live boundary is a
 * plain array index captured at call start; a restore rebuilds the array
 * (possibly with a different length — streamed partials aren't persisted),
 * so the index must be re-derived from row timestamps instead.
 *
 * `createdAt` is server clock and `callStartedAt` client clock — skew can
 * shift the boundary by a few rows, which only affects which turns the
 * transcript rail shows, never the chat itself. Rows without a timestamp
 * are skipped (they can't prove membership); no match → end of list (rail
 * shows only turns that arrive after the restore).
 */
export function callStartIndexFromRestore(
  messages: readonly Message[],
  callStartedAt: number | null
): number {
  if (callStartedAt == null) return messages.length;
  const idx = messages.findIndex(
    (m) => typeof m.createdAt === 'number' && m.createdAt >= callStartedAt
  );
  return idx === -1 ? messages.length : idx;
}
